import React, { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "../../state/authStore";
import { metricsApi, MetricsSnapshot } from "../../api/metrics";

// ─────────────────────────────────────────────────────────────────────────
// WebAdminMetrics — internal / founder-facing operational dashboard.
//
// Gated server-side by ADMIN_EMAILS (env allowlist). The FE doesn't try
// to pre-check whether the user is an admin — it just renders the page
// and surfaces a 403 inline when the parent isn't on the list.
//
// One round-trip on mount, manual refresh button. No real-time streaming
// — these numbers move slowly enough that a button is friendlier than
// a polling loop that drains the laptop.
// ─────────────────────────────────────────────────────────────────────────

export function WebAdminMetrics() {
  const { accessToken } = useAuthStore();
  const [snapshot, setSnapshot] = useState<MetricsSnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const data = await metricsApi.snapshot(accessToken);
      setSnapshot(data);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Couldn't load metrics.",
      );
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    load();
  }, [load]);

  const subRows = snapshot
    ? Object.entries(snapshot.subscriptions).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="fadeup" style={{ padding: "28px 32px 64px" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          marginBottom: 22,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 900,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "var(--primary-ink)",
            }}
          >
            Internal
          </div>
          <h1
            style={{
              fontFamily: "var(--display)",
              fontSize: 36,
              fontWeight: 900,
              letterSpacing: "-0.02em",
              color: "var(--ink)",
              margin: "4px 0 4px",
            }}
          >
            Operational metrics
          </h1>
          <div
            style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-3)" }}
          >
            {snapshot
              ? `Snapshot at ${new Date(snapshot.generatedAt).toLocaleString()}`
              : "Live snapshot — point-in-time, not historical."}
          </div>
        </div>
        <button
          className="btn btn-ghost"
          onClick={load}
          disabled={loading}
          style={{ background: "var(--surface)" }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          className="w-panel"
          style={{
            background: "#FFF7F7",
            border: "1px solid #F5C7C7",
            marginBottom: 18,
          }}
        >
          <h3 style={{ color: "#7A2E2E" }}>Can&apos;t load metrics</h3>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              color: "var(--ink-2)",
              lineHeight: 1.5,
            }}
          >
            {error}
          </div>
          <div
            style={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "var(--ink-3)",
              marginTop: 10,
            }}
          >
            If you&apos;re seeing &ldquo;Admin access required&rdquo;,
            ask whoever runs ops to add your email to{" "}
            <code>ADMIN_EMAILS</code> in <code>backend/.env</code>.
          </div>
        </div>
      )}

      {snapshot && (
        <>
          {/* Parents — top-line KPI grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 14,
              marginBottom: 22,
            }}
          >
            <Stat label="Total parents" value={snapshot.parents.total} />
            <Stat label="DAU" value={snapshot.parents.dau} sub="last 24h" />
            <Stat label="WAU" value={snapshot.parents.wau} sub="last 7 days" />
            <Stat label="MAU" value={snapshot.parents.mau} sub="last 30 days" />
          </div>

          {/* New signups + retention */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 14,
              marginBottom: 22,
            }}
          >
            <Stat
              label="New parents · 7d"
              value={snapshot.parents.newLast7d}
            />
            <Stat
              label="New parents · 30d"
              value={snapshot.parents.newLast30d}
            />
            <Stat
              label="Retention · 7d"
              value={`${snapshot.parents.retentionWeekPct}%`}
              sub={`of ${snapshot.parents.cohortEligible} eligible`}
            />
            <Stat
              label="Retention · 30d"
              value={`${snapshot.parents.retentionMonthPct}%`}
              sub={`of ${snapshot.parents.cohortEligible} eligible`}
            />
          </div>

          {/* Engagement totals */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 14,
              marginBottom: 22,
            }}
          >
            <Stat label="Total kids" value={snapshot.kids.total} />
            <Stat
              label="Sessions (all-time)"
              value={snapshot.sessions.total}
            />
            <Stat
              label="Watched minutes"
              value={snapshot.sessions.totalMinutes}
            />
          </div>

          {/* Subscriptions by Stripe status */}
          <div className="w-panel">
            <h3>Subscriptions by status</h3>
            {subRows.length === 0 ? (
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--ink-3)",
                }}
              >
                No subscription rows yet.
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                }}
              >
                {subRows.map(([status, count]) => (
                  <div
                    key={status}
                    className="w-row"
                    style={{ padding: "12px 0" }}
                  >
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 800,
                        color: "var(--ink)",
                      }}
                    >
                      {status}
                    </div>
                    <div
                      style={{
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, monospace",
                        fontSize: 14,
                        fontWeight: 800,
                        color: "var(--ink)",
                      }}
                    >
                      {count}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--ink-3)",
              marginTop: 18,
              lineHeight: 1.6,
            }}
          >
            DAU / WAU / MAU = distinct parents with{" "}
            <code>lastSeenAt</code> inside the 24h / 7d / 30d window.
            Retention buckets exclude parents who signed up within the
            last 30 days (denominator = parents who&apos;ve had a fair
            chance to come back).
          </div>
        </>
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  sub,
}: {
  label: string;
  value: number | string;
  sub?: string;
}) {
  return (
    <div className="w-stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div className="delta">{sub}</div>}
    </div>
  );
}

export default WebAdminMetrics;
